// Updated Theme

import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const darkBackgroundDefault = '#152326';
const darkBackgroundPaper = '#1d2f34';
const lightBackgroundDefault = '#f8fafc';
const lightBackgroundPaper = '#ffffff';

export const getTheme = (mode = 'light', compact = false) => {
  let theme = createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'dark' ? '#63d8cb' : '#087c74',
        light: '#31c2b7',           // explicit so the hero secondary CTA has a reliable light tone
        contrastText: mode === 'dark' ? '#102b2a' : '#ffffff',
      },
      secondary: { main: mode === 'dark' ? '#63d8cb' : '#087c74' },
      success: { main: mode === 'dark' ? '#77dca8' : '#167548', contrastText: mode === 'dark' ? '#102b2a' : '#ffffff' },
      warning: { main: mode === 'dark' ? '#ffca76' : '#925900', contrastText: mode === 'dark' ? '#102b2a' : '#ffffff' },
      error: { main: mode === 'dark' ? '#ff91a1' : '#bc293c', contrastText: mode === 'dark' ? '#102b2a' : '#ffffff' },
      info: { main: mode === 'dark' ? '#83ccff' : '#096b99', contrastText: mode === 'dark' ? '#102b2a' : '#ffffff' },
      background: {
        default: mode === 'dark' ? darkBackgroundDefault : lightBackgroundDefault,
        paper:   mode === 'dark' ? darkBackgroundPaper : lightBackgroundPaper,
      },
      text: {
        heading:   mode === 'dark' ? '#edf7f4' : '#145653',
        primary:   mode === 'dark' ? '#edf7f4' : '#111827',
        secondary: mode === 'dark' ? '#b8c7c2' : '#374151',
        disabled:  mode === 'dark' ? '#a6b9b4' : '#6b7280',
      },
      divider: mode === 'dark' ? 'rgba(237,247,244,0.12)' : 'rgba(17,24,39,0.10)',
      // keep semantic hover; we’ll style hover colors in components when needed
      action: {
        hover: mode === 'dark' ? 'rgba(237,247,244,0.06)' : 'rgba(17,24,39,0.04)',
        selected: mode === 'dark' ? 'rgba(24,143,135,0.18)' : 'rgba(24,143,135,0.10)',
        disabled: mode === 'dark' ? '#a6b9b4' : '#626c77',
        disabledBackground: mode === 'dark' ? 'rgba(237,247,244,0.08)' : 'rgba(17,24,39,0.08)',
        hoverOpacity: 0.08,
      },
    },

    shape: { borderRadius: 8 },
    spacing: 8,

    typography: {
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontSize: compact ? 12 : 14,
      fontWeightRegular: 400,
      fontWeightMedium:  600,
      fontWeightBold:    700,

      // Premium, confident headings
      h1: { fontSize: compact ? '2.25rem' : '3rem', fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.08 },
      h2: { fontSize: compact ? '1.875rem' : '2.25rem', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.12 },
      h3: { fontSize: compact ? '1.5rem' : '1.875rem', fontWeight: 500, letterSpacing: '-0.02em', lineHeight: 1.18 },
      h4: { fontSize: compact ? '1.375rem' : '1.5rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.22 },
      h5: { fontSize: compact ? '1.125rem' : '1.25rem', fontWeight: 600, lineHeight: 1.28 },
      h6: { fontSize: compact ? '1rem' : '1.125rem', fontWeight: 600, lineHeight: 1.32 },

      subtitle1: { fontSize: compact ? '1rem' : '1.125rem', lineHeight: 1.5, fontWeight: 500 },
      subtitle2: { fontSize: compact ? '0.875rem' : '1rem', lineHeight: 1.45, fontWeight: 500 },
      body1: { fontSize: compact ? '0.95rem' : '1rem', lineHeight: 1.6, fontWeight: 400 },
      body2: { fontSize: '0.875rem', lineHeight: 1.55, fontWeight: 400 },
      caption: { fontSize: '0.75rem', lineHeight: 1.45, fontWeight: 400 },
      overline:  { textTransform: 'none', fontWeight: 600, letterSpacing: '0.04em', fontSize: '0.75rem', lineHeight: 1.35 },

      // Buttons everywhere
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em', lineHeight: 1.2, fontSize: '0.9375rem' },
    },

    components: {
      MuiButtonBase: { styleOverrides: { root: { '&.Mui-focusVisible': { outline: '3px solid', outlineColor: mode === 'dark' ? '#63d8cb' : '#087c74', outlineOffset: 3 } } } },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            minHeight: '100%',
            colorScheme: mode,
            backgroundColor: mode === 'dark' ? darkBackgroundDefault : lightBackgroundDefault,
          },
          body: {
            minHeight: '100%',
            color: mode === 'dark' ? '#edf7f4' : '#111827',
            backgroundColor: mode === 'dark' ? darkBackgroundDefault : lightBackgroundDefault,
          },
          '#root': {
            minHeight: '100vh',
            backgroundColor: mode === 'dark' ? darkBackgroundDefault : lightBackgroundDefault,
          },
          '#root > .App, .App': {
            minHeight: '100vh',
            width: '100%',
            backgroundColor: mode === 'dark' ? darkBackgroundDefault : lightBackgroundDefault,
            color: mode === 'dark' ? '#edf7f4' : '#111827',
          },
          '.omeca-wrapper, .melucra-wrapper': {
            minHeight: '100vh',
            backgroundColor: mode === 'dark' ? darkBackgroundDefault : lightBackgroundDefault,
            color: mode === 'dark' ? '#edf7f4' : '#111827',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: compact ? '8px 14px' : '10px 16px' },
          containedPrimary: { backgroundColor: '#087c74', color: '#ffffff', '&:hover': { backgroundColor: '#06675f' } },
          outlined: {
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(17,24,39,0.14)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            border: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(17,24,39,0.08)'}`,
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? darkBackgroundPaper : lightBackgroundPaper,
            color:          mode === 'dark' ? '#edf7f4' : '#111827',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#b8c7c2' : '#374151',
            '&.Mui-focused': {
              color: mode === 'dark' ? '#63d8cb' : '#087c74',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#a6b9b4' : '#6b7280',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#edf7f4' : '#111827',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(17,24,39,0.14)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(17,24,39,0.22)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: mode === 'dark' ? '#63d8cb' : '#087c74',
            },
            '& input::placeholder': {
              color: mode === 'dark' ? '#a6b9b4' : '#6b7280',
              opacity: 1,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            color: mode === 'dark' ? '#edf7f4' : '#111827',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: mode === 'dark' ? '#b8c7c2' : '#374151',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? '#edf7f4' : '#111827',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: mode === 'dark' ? 'rgba(237,247,244,0.10)' : 'rgba(17,24,39,0.10)',
            color: mode === 'dark' ? '#edf7f4' : '#111827',
          },
          head: {
            backgroundColor: mode === 'dark' ? 'rgba(237,247,244,0.04)' : 'rgba(17,24,39,0.035)',
            color: mode === 'dark' ? '#d7e4df' : '#111827',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(24,143,135,0.18)' : 'rgba(24,143,135,0.10)',
            },
            '&.Mui-selected:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(24,143,135,0.24)' : 'rgba(24,143,135,0.16)',
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: mode === 'dark' ? '#edf7f4' : '#111827',
          },
          secondary: {
            color: mode === 'dark' ? '#b8c7c2' : '#374151',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          label: { fontWeight: 600, letterSpacing: '0.01em' },
        },
      },
      MuiTypography: {
        defaultProps: {
          color: 'text.primary',
        },
      },
    },
  });

  theme = responsiveFontSizes(theme, { factor: 2 });
  return theme;
};
